/**
 * Superadmin Onboarding Management
 *
 * Reset org-level checklists and map-level getting started wizards.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Input,
  Label,
} from "@wondermaps/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft } from "@fortawesome/pro-solid-svg-icons/faRotateLeft";
import { faBuilding } from "@fortawesome/pro-solid-svg-icons/faBuilding";
import { faMap } from "@fortawesome/pro-solid-svg-icons/faMap";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons/faTrashCan";
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { useMapGettingStartedStore } from "../stores/mapGettingStarted";
import { SuperadminHeader } from "../components/superadmin/SuperadminHeader";
import type { OrgSettings } from "@wondermaps/db";

export const Route = createFileRoute("/superadmin/onboarding")({
  component: SuperadminOnboarding,
});

type Organization = {
  id: string;
  name: string;
  slug: string;
  type: string | null;
  memberCount: number;
  createdAt: string;
};

function SuperadminOnboarding() {
  const { maps: mapStates, resetMap } = useMapGettingStartedStore();

  // Fetch organizations
  const { data: orgsData, isLoading: orgsLoading } = useQuery({
    queryKey: ["superadmin", "orgs"],
    queryFn: async () => {
      const res = await api.orgs.admin.$get();
      if (!res.ok) throw new Error("Failed to fetch organizations");
      return res.json() as Promise<{ orgs: Organization[] }>;
    },
  });

  const orgs = orgsData?.orgs ?? [];

  // Get all map IDs from localStorage state
  const mapIds = Object.keys(mapStates);

  return (
    <div className="flex h-screen flex-col bg-background">
      <SuperadminHeader
        title="Onboarding Management"
        subtitle="Reset org checklists and map wizards for testing"
      />

      <main className="flex-1 overflow-auto p-6">
        <div className="container mx-auto max-w-4xl space-y-6">
          {/* Org-Level Checklists */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FontAwesomeIcon icon={faBuilding} className="h-5 w-5" />
                Organization Checklists
              </CardTitle>
              <CardDescription>
                Reset onboarding checklist state for organizations
              </CardDescription>
            </CardHeader>
            <CardContent>
              {orgsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : orgs.length === 0 ? (
                <p className="text-muted-foreground text-sm">No organizations found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Organization</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orgs.map((org) => (
                      <OrgRow key={org.id} org={org} />
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Map-Level Wizards (localStorage) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FontAwesomeIcon icon={faMap} className="h-5 w-5" />
                Map Getting Started Wizards
              </CardTitle>
              <CardDescription>
                Reset getting started wizard state (stored in localStorage)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {mapIds.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  No map wizard states found in localStorage
                </p>
              ) : (
                <div className="space-y-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Map ID</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mapIds.map((mapId) => {
                        const state = mapStates[mapId];
                        return (
                          <TableRow key={mapId}>
                            <TableCell className="font-mono text-xs">
                              {mapId.slice(0, 8)}...
                            </TableCell>
                            <TableCell className="text-sm">
                              {state.dismissed ? (
                                <span className="text-red-500">Dismissed</span>
                              ) : state.collapsed ? (
                                <span className="text-yellow-500">Collapsed</span>
                              ) : (
                                <span className="text-green-500">Visible</span>
                              )}
                              {" • "}
                              {state.completedItems.length}/4 items
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => resetMap(mapId)}
                              >
                                <FontAwesomeIcon icon={faRotateLeft} className="h-3.5 w-3.5 mr-1" />
                                Reset
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      mapIds.forEach((id) => resetMap(id));
                    }}
                  >
                    <FontAwesomeIcon icon={faTrashCan} className="h-3.5 w-3.5 mr-1" />
                    Reset All Map Wizards
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Manual Map ID Reset */}
          <Card>
            <CardHeader>
              <CardTitle>Manual Map Reset</CardTitle>
              <CardDescription>
                Enter a map ID to reset its wizard state
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ManualMapReset />
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

function OrgRow({ org }: { org: Organization }) {
  const queryClient = useQueryClient();

  // Fetch org settings to show current state
  const { data: settings } = useQuery({
    queryKey: ["org-settings", org.id],
    queryFn: async () => {
      const res = await api.orgs[":orgId"].settings.$get({
        param: { orgId: org.id },
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data.settings as OrgSettings;
    },
  });

  const checklist = settings?.onboarding_checklist;

  // Reset mutation
  const resetMutation = useMutation({
    mutationFn: async () => {
      const res = await api.orgs[":orgId"].settings.$patch({
        param: { orgId: org.id },
        json: {
          onboarding_checklist: {
            org_profile_completed: false,
            qr_downloaded: false,
            analytics_viewed: false,
            checklist_dismissed: false,
          },
        },
      });
      if (!res.ok) throw new Error("Failed to reset");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-settings", org.id] });
    },
  });

  const completedCount = [
    checklist?.org_profile_completed,
    checklist?.qr_downloaded,
    checklist?.analytics_viewed,
  ].filter(Boolean).length;

  return (
    <TableRow>
      <TableCell className="font-medium">{org.name}</TableCell>
      <TableCell className="text-muted-foreground">{org.slug}</TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          <span className="text-xs text-muted-foreground">
            {checklist?.checklist_dismissed ? (
              <span className="text-red-500">Dismissed</span>
            ) : (
              <span>{completedCount}/3 stored</span>
            )}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => resetMutation.mutate()}
            disabled={resetMutation.isPending}
          >
            <FontAwesomeIcon icon={faRotateLeft} className="h-3.5 w-3.5 mr-1" />
            Reset
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function ManualMapReset() {
  const [mapId, setMapId] = useState("");
  const { resetMap } = useMapGettingStartedStore();
  const [success, setSuccess] = useState(false);

  const handleReset = () => {
    if (!mapId.trim()) return;
    resetMap(mapId.trim());
    setSuccess(true);
    setTimeout(() => setSuccess(false), 2000);
    setMapId("");
  };

  return (
    <div className="flex items-end gap-2">
      <div className="flex-1 space-y-1">
        <Label htmlFor="map-id">Map ID</Label>
        <Input
          id="map-id"
          placeholder="Enter map UUID"
          value={mapId}
          onChange={(e) => setMapId(e.target.value)}
        />
      </div>
      <Button onClick={handleReset} disabled={!mapId.trim()}>
        {success ? (
          <>
            <FontAwesomeIcon icon={faCheck} className="h-4 w-4 mr-1" />
            Done
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faRotateLeft} className="h-4 w-4 mr-1" />
            Reset
          </>
        )}
      </Button>
    </div>
  );
}
